import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableHead from '@mui/material/TableHead';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import EditIcon from '@mui/icons-material/Edit';

import { StyledTableCell, StyledTableRow } from '../../../components/StyledTableComponents';
import { TPaymentTerminalProvider, TFieldSchema } from '../schemas/payment-provider-schema';

type ProvidersClientSummaryProps = {
  providers: TPaymentTerminalProvider[];
  onEdit: (provider: TPaymentTerminalProvider) => void;
};

const formatLabel = (id: string) =>
  id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const clientFieldsOf = (provider: TPaymentTerminalProvider): TFieldSchema[] =>
  (provider.schema?.fields || [])
    .filter((f) => f.scope === 'client')
    .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999));

const ProvidersClientSummary = ({ providers, onEdit }: ProvidersClientSummaryProps) => {
  if (!providers || providers.length === 0) {
    return (
      <Box sx={styles.emptyContainer}>
        <Typography sx={styles.emptyText}>No payment providers registered yet.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={styles.tableContainer}>
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <StyledTableRow>
            <StyledTableCell>Provider</StyledTableCell>
            <StyledTableCell>Provider ID</StyledTableCell>
            <StyledTableCell>Client Fields Expected</StyledTableCell>
            <StyledTableCell align="right">Actions</StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody>
          {providers.map((provider) => {
            const clientFields = clientFieldsOf(provider);
            return (
              <StyledTableRow key={provider.providerId}>
                <StyledTableCell sx={styles.providerCell}>
                  {provider.displayName || provider.providerId}
                </StyledTableCell>
                <StyledTableCell sx={styles.monoCell}>{provider.providerId}</StyledTableCell>
                <StyledTableCell>
                  {clientFields.length === 0 ? (
                    <Typography sx={styles.muted}>—</Typography>
                  ) : (
                    <Box sx={styles.fieldChips}>
                      {clientFields.map((f) => (
                        <Chip
                          key={f.id}
                          size="small"
                          label={
                            <Box sx={styles.chipInner}>
                              <span style={styles.chipLabel}>
                                {f.label || formatLabel(f.id)}
                              </span>
                              {f.required && (
                                <span style={styles.chipReq}>req</span>
                              )}
                              {f.type === 'secret' && (
                                <span style={styles.chipSecret}>secret</span>
                              )}
                            </Box>
                          }
                          sx={styles.chip}
                        />
                      ))}
                    </Box>
                  )}
                </StyledTableCell>
                <StyledTableCell align="right">
                  <Tooltip title="Manage provider schema">
                    <IconButton size="small" onClick={() => onEdit(provider)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </StyledTableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProvidersClientSummary;

const styles = {
  tableContainer: {
    boxShadow: 'none',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  emptyContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    borderRadius: '8px',
    height: '6rem',
  },
  emptyText: {
    color: '#999',
    fontSize: '14px',
    fontFamily: 'InterRegular',
  },
  providerCell: {
    fontFamily: 'GilroySemiBold',
  },
  monoCell: {
    fontFamily: 'monospace',
    fontSize: 13,
    color: '#555',
  },
  muted: {
    color: '#94a3b8',
    fontSize: 13,
  },
  fieldChips: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: 0.75,
  },
  chip: {
    backgroundColor: '#f1f5f9',
    border: '1px solid #e2e8f0',
    height: 'auto',
    py: 0.25,
    '& .MuiChip-label': {
      px: 1,
    },
  },
  chipInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
  },
  chipLabel: {
    fontFamily: 'GilroySemiBold',
    fontSize: 12,
    color: '#0f172a',
  },
  chipReq: {
    fontFamily: 'GilroyBold',
    fontSize: 9,
    color: '#991b1b',
    backgroundColor: '#fee2e2',
    padding: '1px 5px',
    borderRadius: 4,
  },
  chipSecret: {
    fontFamily: 'GilroyBold',
    fontSize: 9,
    color: '#92400e',
    backgroundColor: '#fef3c7',
    padding: '1px 5px',
    borderRadius: 4,
  },
};
